import { useAuthenticatedMutation, useAuthenticatedQuery } from 'client/hooks';
import { useAppState } from 'client/providers/AppProvider';
import {
    GetAllProfileViewDataForCollegesQuery,
    GetAllProfileViewDataForCollegesQueryVariables,
    GetAllProfileViewDataForCollegesDocument,
    Espin_Profile_View_Type_Enum,
    FollowPlayerDocument,
    FollowPlayerMutation,
    FollowPlayerMutationVariables,
    UnfollowPlayerDocument,
    UnfollowPlayerMutation,
    UnfollowPlayerMutationVariables,
    FollowMutation,
    FollowMutationVariables,
    UnfollowMutation,
    UnfollowMutationVariables,
    UnfollowDocument,
    FollowDocument,
    Espin_Follow_Type_Enum,
} from 'generated/spin-graphql';
import React, { useCallback, useMemo } from 'react';
import { gql } from 'urql';
import { ProfileViews } from './ProfileViews';
gql`
    query GetAllProfileViewDataForColleges($id: uuid!, $vt: [espin_profile_view_type_enum!], $date: timestamptz) {
        espin_follow(
            where: {
                college_id: { _eq: $id }
                follow_type: { _in: [college_follows_player, college_follows_high_school] }
            }
        ) {
            player_user_id
            high_school_id
        }
        espin_profile_view(
            where: { college_id: { _eq: $id }, profile_view_type: { _in: $vt } }
            order_by: { created_at: desc }
        ) {
            id
            ...ProfileViews
        }
        espin_profile_view_aggregate(
            where: { college_id: { _eq: $id }, profile_view_type: { _in: $vt }, created_at: { _gt: $date } }
        ) {
            aggregate {
                count
            }
        }
    }
`;
const backUpDate = new Date().toISOString();
const CollegeProfileViews = () => {
    const { user } = useAppState();

    const { isReady, data } = useAuthenticatedQuery<
        GetAllProfileViewDataForCollegesQuery,
        GetAllProfileViewDataForCollegesQueryVariables
    >(
        {
            query: GetAllProfileViewDataForCollegesDocument,
            variables: {
                id: user?.college_profile?.college.id,
                vt: [
                    Espin_Profile_View_Type_Enum.ParentViewsCollege,
                    Espin_Profile_View_Type_Enum.PlayerViewsCollege,
                    Espin_Profile_View_Type_Enum.HighSchoolViewsCollege,
                ],
                date: user.last_login ?? backUpDate,
            },
            pause: !user,
        },
        ['espin_follow'],
    );
    const [_, follow] = useAuthenticatedMutation<FollowMutation, FollowMutationVariables>(FollowDocument);

    const [__, unFollow] = useAuthenticatedMutation<UnfollowMutation, UnfollowMutationVariables>(UnfollowDocument);
    const onUnfollow = useCallback(
        (userId: string, viewType: Espin_Profile_View_Type_Enum) => {
            unFollow(
                {
                    where: {
                        college_id: { _eq: user?.college_profile?.college.id },
                        follow_type: {
                            _eq:
                                viewType === Espin_Profile_View_Type_Enum.HighSchoolViewsCollege
                                    ? Espin_Follow_Type_Enum.CollegeFollowsHighSchool
                                    : Espin_Follow_Type_Enum.CollegeFollowsPlayer,
                        },
                        ...(viewType !== Espin_Profile_View_Type_Enum.HighSchoolViewsCollege && {
                            player_user_id: { _eq: userId },
                        }),
                        ...(viewType === Espin_Profile_View_Type_Enum.HighSchoolViewsCollege && {
                            high_school_id: { _eq: userId },
                        }),
                    },
                },
                { additionalTypenames: ['espin_follow'] },
            );
        },
        [unFollow, user?.college_profile?.college?.id],
    );

    const onFollow = useCallback(
        (userId: string, viewType: Espin_Profile_View_Type_Enum) => {
            follow(
                {
                    input: {
                        college_id: user?.college_profile?.college.id,
                        college_user_name: `${user?.college_profile?.first_name} ${user?.college_profile?.last_name}`,
                        ...(viewType !== Espin_Profile_View_Type_Enum.HighSchoolViewsCollege && {
                            player_user_id: userId,
                        }),
                        ...(viewType === Espin_Profile_View_Type_Enum.HighSchoolViewsCollege && {
                            high_school_id: userId,
                        }),
                        follow_type:
                            viewType === Espin_Profile_View_Type_Enum.HighSchoolViewsCollege
                                ? Espin_Follow_Type_Enum.CollegeFollowsHighSchool
                                : Espin_Follow_Type_Enum.CollegeFollowsPlayer,
                    },
                },
                { additionalTypenames: ['espin_follow'] },
            );
        },
        [
            follow,
            user?.college_profile?.college.id,
            user?.college_profile?.first_name,
            user?.college_profile?.last_name,
        ],
    );
    const listOfFollows = useMemo(() => data?.espin_follow.map((f) => f.player_user_id ?? f.high_school_id), [data]);

    return (
        <div>
            {isReady && (
                <ProfileViews
                    listOfFollows={listOfFollows}
                    profileViews={data?.espin_profile_view}
                    totalViews={data?.espin_profile_view.length}
                    newViews={data?.espin_profile_view_aggregate.aggregate.count}
                    onFollow={onFollow}
                    onUnfollow={onUnfollow}
                />
            )}
        </div>
    );
};
export default CollegeProfileViews;
